import { forwardRef, useState } from 'react';
import {
  PixelRatio,
  Platform,
  Text as RNText,
  TextInput as RNTextInput,
  View,
  StyleSheet,
  type TextProps,
  type TextInput as RNTextInputType,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { useFont, type FontFamily } from '@/providers/FontProvider';
import { useLocale } from '@/hooks/useLocale';
import { inputDirectionStyle } from '@/lib/input-direction';

/**
 * App-wide font-aware Text / TextInput.
 *
 * These wrap React Native's primitives and resolve the correct locale font
 * (Google Sans for Latin, Cairo for Arabic: via `useFont()`) from the style's
 * `fontWeight`, so every label/input renders in the brand font instead of the
 * platform system font.
 *
 * Rules:
 *  - If a caller already sets an explicit `fontFamily` (icon fonts, monospace,
 *    a custom face), it is left untouched.
 *  - Otherwise `fontWeight` maps to a named font face:
 *      <=400 / 'normal'  → regular
 *      500   / 'medium'  → medium
 *      600   / 'semibold'→ semibold
 *      >=700 / 'bold'    → bold
 *  - On web these native face names fall back to the Google Sans/Cairo body
 *    stack applied by FontProvider, matching the existing `fonts.*` usage.
 *  - Any style with a numeric `fontSize` gets the OS accessibility text-size
 *    setting clamped to [0.92x, 1.15x] instead of RN's uncapped scaling (see
 *    `clampedFontScale`), so the app doesn't swing from unreadably tiny to
 *    oversized the way plain `allowFontScaling` does.
 *
 * Import `Text`/`TextInput` from here instead of from `react-native`
 * (enforced by the `no-restricted-imports` ESLint rule).
 */
function familyForWeight(
  fonts: FontFamily,
  weight: TextStyle['fontWeight'],
): string | undefined {
  switch (weight) {
    case 500:
    case '500':
    case 'medium':
      return fonts.medium;
    case 600:
    case '600':
    case 'semibold':
      return fonts.semibold;
    case 700:
    case '700':
    case 800:
    case '800':
    case 900:
    case '900':
    case 'bold':
      return fonts.bold;
    default:
      // undefined, 'normal', 100–400 → regular
      return fonts.regular;
  }
}

// Bound how far the OS "Larger/Smaller Text" accessibility setting can shrink
// or grow app text. Native `allowFontScaling` has no floor and no ceiling of
// its own, so left alone a Text at fontSize 12 can render anywhere from ~7px
// to ~35px depending on the device setting. Reference apps like Gemini keep
// this range tight instead. Applied only where a style sets an explicit
// numeric `fontSize` (the design system's `fontSize`/`headingSize` tokens
// cover virtually every styled Text); text with no explicit size falls back
// to RN's own default scaling, which is a rare, low-stakes case.
const MIN_FONT_SCALE = 0.92;
const MAX_FONT_SCALE = 1.15;

function clampedFontScale(): number {
  return Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, PixelRatio.getFontScale()));
}

// Text overlays must use the same scale as the native text they cover.
export function applyBoundedFontScale(style: TextStyle | undefined): TextStyle | undefined {
  if (typeof style?.fontSize !== 'number') return undefined;
  const scale = clampedFontScale();
  return {
    fontSize: style.fontSize * scale,
    lineHeight: typeof style.lineHeight === 'number' ? style.lineHeight * scale : undefined,
  };
}

// Android only: Android sizes every text line by the font's most extreme
// glyph metrics (`includeFontPadding: true` is the platform default). Google
// Sans's win/bbox metrics span ~2.25em against its 1.25em ascent/descent, so
// each line box gains ~1em of *asymmetric* padding: labels render ~3px above
// the centerline of icon rows, and fixed-height TextInputs become vertically
// scrollable because the line is taller than the box. iOS and web size lines
// by ascent/descent only, which is why only Android misrenders. Disabling the
// padding restores cross-platform parity. First in the style array: callers
// can still opt back in with `includeFontPadding: true`.
const ANDROID_FONT_PADDING_RESET: TextStyle | null =
  Platform.OS === 'android' ? { includeFontPadding: false } : null;

// Re-export the underlying RN instance types under the same names so that
// `useRef<Text>()` / `useRef<TextInput>()` keep resolving after a call site
// swaps its value import to this module. A name can be both a value and a type.
export type Text = RNText;
export type TextInput = RNTextInputType;

export const Text = forwardRef<RNText, TextProps>(function Text(
  { style, allowFontScaling, ...rest },
  ref,
) {
  const { fonts } = useFont();
  const flattened = StyleSheet.flatten(style) as TextStyle | undefined;
  const boundedStyle = applyBoundedFontScale(flattened);
  // A caller-supplied allowFontScaling always wins; otherwise our own
  // clamped scale (via boundedStyle) replaces RN's uncapped native scaling.
  const scaling = allowFontScaling ?? (boundedStyle ? false : undefined);

  // Respect an explicitly-provided font family (icon fonts, monospace, custom).
  if (flattened?.fontFamily) {
    return (
      <RNText
        ref={ref}
        allowFontScaling={scaling}
        style={[ANDROID_FONT_PADDING_RESET, style, boundedStyle]}
        {...rest}
      />
    );
  }

  const fontFamily = familyForWeight(fonts, flattened?.fontWeight);
  return (
    <RNText
      ref={ref}
      allowFontScaling={scaling}
      style={[ANDROID_FONT_PADDING_RESET, { fontFamily }, style, boundedStyle]}
      {...rest}
    />
  );
});
Text.displayName = 'Text';

// Web only: react-native-web stopped suppressing the browser's native focus
// ring, so every TextInput grew Chrome's blue :focus-visible outline. Our
// inputs sit inside custom-styled fields with their own focus affordances, so
// restore the outline-free look. First in the style array: callers can still
// opt back in with their own outlineStyle. ('none' is valid on web; RN's
// TextStyle type only lists the native-supported values, hence the cast.)
const WEB_INPUT_RESET =
  Platform.OS === 'web' ? ({ outlineStyle: 'none' } as unknown as TextStyle) : null;

// Arabic-script ranges: Arabic, Supplement, Extended-A, and the two
// Presentation-Forms blocks. Used to detect placeholders that hit the iOS bug.
const ARABIC_RE = new RegExp(
  '[\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF]',
);

// How the input sits in its *parent* (flex/align/margins/width) moves to the
// overlay wrapper so the wrapper takes the input's slot. Everything that shapes
// the input's own box: height, padding, borders, background, colours, font -
// stays on the input, which then stretches to fill the wrapper's width. Height
// deliberately stays put so inputs that draw their own bordered/filled box at a
// fixed height are visually unchanged. Padding is also copied to the overlay so
// the placeholder lands at the same inset as typed text.
const LAYOUT_KEYS = [
  'flex', 'flexGrow', 'flexShrink', 'flexBasis', 'alignSelf',
  'width', 'minWidth', 'maxWidth',
  'margin', 'marginTop', 'marginBottom', 'marginLeft', 'marginRight',
  'marginHorizontal', 'marginVertical', 'marginStart', 'marginEnd',
] as const;
const PADDING_KEYS = [
  'padding', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
  'paddingHorizontal', 'paddingVertical', 'paddingStart', 'paddingEnd',
] as const;

function pickStyle(style: TextStyle | undefined, keys: readonly string[]): ViewStyle {
  const out: Record<string, unknown> = {};
  if (!style) return out;
  for (const key of keys) {
    const value = (style as Record<string, unknown>)[key];
    if (value !== undefined) out[key] = value;
  }
  return out as ViewStyle;
}

type AppTextInputProps = TextInputProps & {
  /**
   * Extra inset added only to the iOS Arabic-placeholder overlay text (not the
   * real input box), on whichever edge it's aligned to. Use to clear native
   * iOS decorations that draw over the real input: e.g. the secure-field
   * caps-lock badge: without shrinking the input's own tappable box.
   */
  placeholderInsetEnd?: number;
};

export const TextInput = forwardRef<RNTextInputType, AppTextInputProps>(
  function TextInput({ style, placeholderInsetEnd, allowFontScaling, ...rest }, ref) {
    const { fonts, arabicFonts } = useFont();
    const { isRTL } = useLocale();
    // Defaults cover every shared form/search input. Callers retain explicit
    // code, URL, centered OTP, and content-aware alignment overrides.
    const inputDefaults = inputDirectionStyle(isRTL, rest);
    const flattened = StyleSheet.flatten([inputDefaults, style]) as TextStyle;
    // Empty-state tracking for the iOS Arabic-placeholder overlay (below). Kept
    // as a hook at the top so it's called unconditionally; unused otherwise.
    const [uncontrolledText, setUncontrolledText] = useState(
      typeof rest.defaultValue === 'string' ? rest.defaultValue : '',
    );

    const explicitFamily = flattened?.fontFamily;
    const fontFamily = explicitFamily ?? familyForWeight(fonts, flattened?.fontWeight);
    const boundedStyle = applyBoundedFontScale(flattened);
    const scaling = allowFontScaling ?? (boundedStyle ? false : undefined);

    // iOS renders Arabic TextInput *placeholders* with disconnected, unjoined
    // glyphs when a custom font is applied: the placeholder draw path drops the
    // Arabic shaping (typed text and <Text> render correctly). `textAlign` /
    // `writingDirection` don't fix it. Render the placeholder as a <Text>
    // overlay instead, which shapes correctly. Scoped to iOS + Arabic-script
    // placeholders so every other input keeps the exact original code path.
    const placeholder = rest.placeholder;
    const needsArabicPlaceholder =
      Platform.OS === 'ios' &&
      typeof placeholder === 'string' &&
      ARABIC_RE.test(placeholder);

    if (!needsArabicPlaceholder) {
      const baseStyle = explicitFamily
        ? [WEB_INPUT_RESET, ANDROID_FONT_PADDING_RESET, inputDefaults, style, boundedStyle]
        : [WEB_INPUT_RESET, ANDROID_FONT_PADDING_RESET, inputDefaults, { fontFamily }, style, boundedStyle];
      return (
        <RNTextInput ref={ref} allowFontScaling={scaling} style={baseStyle} {...rest} />
      );
    }

    const {
      value,
      onChangeText,
      placeholderTextColor,
      multiline,
      placeholder: _placeholder,
      ...restInput
    } = rest;

    const text = value !== undefined ? value : uncontrolledText;
    const handleChangeText = (next: string) => {
      if (value === undefined) setUncontrolledText(next);
      onChangeText?.(next);
    };

    // Move the outer-box layout props onto the wrapper; keep the rest (padding,
    // colours) on the input so its visual box is unchanged. WEB_INPUT_RESET is
    // web-only (null here), so it's omitted on this iOS-only path.
    const inputStyle: TextStyle = { ...(flattened ?? {}) };
    for (const key of LAYOUT_KEYS) delete (inputStyle as Record<string, unknown>)[key];
    if (!explicitFamily) inputStyle.fontFamily = fontFamily;
    inputStyle.alignSelf = 'stretch';
    if (boundedStyle) Object.assign(inputStyle, boundedStyle);

    // Right-align (place at the trailing edge) for RTL layouts or an explicit
    // right textAlign; the placeholder is Arabic, so writingDirection is rtl.
    const rightAligned =
      flattened?.textAlign === 'right' ||
      (flattened?.textAlign !== 'left' &&
        flattened?.textAlign !== 'center' &&
        isRTL);

    return (
      <View style={[{ position: 'relative' }, pickStyle(flattened, LAYOUT_KEYS)]}>
        <RNTextInput
          ref={ref}
          {...restInput}
          allowFontScaling={scaling}
          multiline={multiline}
          value={value}
          onChangeText={handleChangeText}
          placeholder={undefined}
          placeholderTextColor={placeholderTextColor}
          style={inputStyle}
        />
        {!text ? (
          <View
            pointerEvents="none"
            style={[
              StyleSheet.absoluteFill,
              pickStyle(flattened, PADDING_KEYS),
              {
                justifyContent: multiline ? 'flex-start' : 'center',
                // Let Text's physical textAlign place the glyphs. flex-end is
                // the left edge in an RTL column and would flip them twice.
                alignItems: 'stretch',
              },
            ]}
          >
            <Text
              numberOfLines={multiline ? undefined : 1}
              style={{
                color: placeholderTextColor as TextStyle['color'],
                // The placeholder is Arabic script by definition (that is what
                // routed us here), so it gets the Arabic face regardless of the
                // input's own family. In a Latin face the glyphs fall back to
                // a taller system font and get clipped at the top of the line.
                fontFamily: familyForWeight(arabicFonts, flattened?.fontWeight),
                fontSize: flattened?.fontSize,
                lineHeight: flattened?.lineHeight,
                letterSpacing: flattened?.letterSpacing,
                // iOS Fabric mirrors Text's left/right alignment in an RTL
                // paragraph (unlike TextInput). Isolate only the text box so
                // its alignment matches the input without swapping padding.
                direction: 'ltr',
                paddingStart: !rightAligned ? placeholderInsetEnd : undefined,
                paddingEnd: rightAligned ? placeholderInsetEnd : undefined,
                textAlign: flattened.textAlign === 'center' ? 'center' : rightAligned ? 'right' : 'left',
                writingDirection: 'rtl',
              }}
            >
              {placeholder}
            </Text>
          </View>
        ) : null}
      </View>
    );
  },
);
TextInput.displayName = 'TextInput';
